import React from "react";
import type { MenuProps } from "antd";
import type { Menu as AppMenu } from "../service/menu/types";
import {
  getMenuDisplayTitle,
  homeShellDirMenuKey,
  homeShellLeafMenuKey,
  sortVisibleMenus,
} from "./homeShellSidebar";
import { isMenuSvgIconCode, MenuSvgIcon } from "./menuIconRegistry";

function buildMenuIcon(menu: AppMenu.Menu): React.ReactNode {
  if (!menu.meta?.icon || menu.meta.icon === "#" || menu.meta.icon === "") return null;
  const rawIcon = menu.meta.icon.trim();
  if (isMenuSvgIconCode(rawIcon)) {
    return <MenuSvgIcon code={rawIcon} />;
  }
  let iconClass = rawIcon;
  if (!iconClass.match(/^(fas|far|fal|fab)\s/)) {
    if (iconClass.startsWith("fa-")) {
      iconClass = `fas ${iconClass}`;
    } else if (!iconClass.includes("fa-")) {
      iconClass = `fas fa-${iconClass}`;
    }
  }
  return <i className={iconClass} />;
}

/** 判断一个菜单节点在侧边栏中应被视为目录（可展开）还是叶子（可点击） */
function isMenuDirectory(menu: AppMenu.Menu): boolean {
  // 用 type 字段判断：1=DIR 是目录，2=MENU 是叶子，3=BUTTON 应跳过
  if (menu.type !== undefined) {
    return menu.type === 1;
  }
  // 兼容无 type 的旧数据：有 children 则为目录
  return !!(menu.children && menu.children.length > 0);
}

/**
 * 将后端菜单树转为 Ant Design `Menu` 的 `items`（含多级 SubMenu），与个人中心侧栏规则一致。
 * 使用后端 `type`、`alwaysShow` 字段驱动渲染逻辑。
 */
export function convertHomeShellMenusToAntdItems(menus: AppMenu.Menu[]): MenuProps["items"] {
  const sortedMenus = sortVisibleMenus(menus);

  return sortedMenus
    .map((menu) => {
      const menuTitle = getMenuDisplayTitle(menu);
      const isDisabled = menuTitle.includes("开发中") || menu.hidden;
      if (menuTitle.includes("开发中")) return null;
      // 按钮类型不在侧边栏渲染
      if (menu.type === 3) return null;

      const iconElement = buildMenuIcon(menu);

      const sortedChildren =
        menu.children && menu.children.length > 0 ? sortVisibleMenus(menu.children) : [];

      // 目录节点：递归生成 children
      if (isMenuDirectory(menu)) {
        if (sortedChildren.length === 0) return null;

        // alwaysShow 为 true 且仅有一个子项→不包 SubMenu，直接展开子项
        if (menu.alwaysShow && sortedChildren.length === 1) {
          return convertHomeShellMenusToAntdItems(sortedChildren)?.[0] ?? null;
        }

        const childrenItems = convertHomeShellMenusToAntdItems(sortedChildren);
        if (!childrenItems || childrenItems.length === 0) {
          return null;
        }
        return {
          key: homeShellDirMenuKey(menu),
          label: menuTitle,
          icon: iconElement,
          disabled: isDisabled,
          children: childrenItems,
        };
      }

      // 叶子节点：必须有 path
      if (!menu.path || menu.path === "") {
        return null;
      }

      const lk = homeShellLeafMenuKey(menu);
      if (!lk) return null;

      return {
        key: lk,
        label: menuTitle,
        icon: iconElement,
        disabled: isDisabled,
      };
    })
    .filter((item) => item !== null) as MenuProps["items"];
}
